import { z } from "zod";
import { dashboardWarningSchema, dashboardWidgetSchema } from "./dashboard";
import {
  dataStateSchema,
  freshnessSchema,
  qualitySchema,
  reconciliationSchema,
} from "./governance";
import { isoDateTimeSchema, opaqueIdSchema, versionSchema } from "./primitives";

export const safetyReviewCategorySchema = z.enum([
  "warranty_signals",
  "field_actions",
  "regulatory_evidence",
  "campaign_completion",
]);

const safetyReviewEntitySchema = z
  .object({
    id: opaqueIdSchema,
    code: z.string().trim().min(1).max(200).optional(),
    name: z.string().trim().min(1).max(500),
  })
  .strict();

const safetyReviewProblemRecordSchema = z
  .object({
    id: opaqueIdSchema,
    displayId: z.string().trim().min(1).max(200),
    title: z.string().trim().min(1).max(500),
    status: z.enum(["DRAFT", "ACTIVE", "ON_HOLD", "ESCALATED", "CLOSED"]),
  })
  .strict();

const safetyReviewProvenanceSchema = z
  .object({
    sourceConnectionId: opaqueIdSchema,
    ingestionRunId: opaqueIdSchema,
    sourceIdentifier: z.string().trim().min(1).max(500),
    sourceName: z.string().trim().min(1).max(300),
    authorityClass: z.string().trim().min(1).max(100),
  })
  .strict();

export const safetyReviewSignalSchema = z
  .object({
    id: opaqueIdSchema,
    key: z.string().trim().min(1).max(300),
    type: z.string().trim().min(1).max(100),
    title: z.string().trim().min(1).max(500),
    status: z.string().trim().min(1).max(100),
    severity: z.string().trim().min(1).max(100).optional(),
    classification: z.literal("DECISION_SUPPORT_ONLY"),
    product: safetyReviewEntitySchema.optional(),
    component: safetyReviewEntitySchema.optional(),
    problemRecord: safetyReviewProblemRecordSchema.optional(),
    provenance: z.array(safetyReviewProvenanceSchema).min(1).max(100),
    dataState: dataStateSchema,
    authorityClass: z.string().trim().min(1).max(100),
    productionUseProhibited: z.boolean(),
    freshness: freshnessSchema,
    quality: qualitySchema,
    reconciliation: reconciliationSchema,
    sourceSummary: z.string().trim().min(1).max(1_000),
    detectedAt: isoDateTimeSchema,
    dataCutoffAt: isoDateTimeSchema,
  })
  .strict()
  .superRefine((signal, context) => {
    if (
      signal.dataState === "SYNTHETIC" &&
      (signal.authorityClass !== "DEMONSTRATION_ONLY" || !signal.productionUseProhibited)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["authorityClass"],
        message:
          "Synthetic safety-review records must be demonstration-only and prohibited in production.",
      });
    }
  });

export const safetyReviewCategoryPayloadSchema = z
  .object({
    category: safetyReviewCategorySchema,
    label: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(1_000),
    items: z.array(safetyReviewSignalSchema).max(100),
    unavailableReason: z.string().trim().min(1).max(1_000).optional(),
  })
  .strict();

export const safetyReviewWidgetSchema = dashboardWidgetSchema(safetyReviewCategoryPayloadSchema);

export const safetyReviewResponseSchema = z
  .object({
    view: z.literal("safety-review"),
    schemaVersion: versionSchema,
    tenantId: opaqueIdSchema,
    generatedAt: isoDateTimeSchema,
    dataAsOf: isoDateTimeSchema,
    widgets: z.array(safetyReviewWidgetSchema).length(4),
    warnings: z.array(dashboardWarningSchema).max(100).default([]),
  })
  .strict()
  .superRefine((response, context) => {
    const categories = response.widgets.map((widget) => widget.payload.category);
    if (new Set(categories).size !== categories.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["widgets"],
        message: "Safety-review category widgets must be unique.",
      });
    }
    for (const widget of response.widgets) {
      if (widget.dataState === "UNAVAILABLE" && widget.payload.items.length > 0) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["widgets"],
          message: "Unavailable safety-review categories cannot contain records.",
        });
      }
      if (
        widget.payload.items.some(
          (item) =>
            item.dataState === "SYNTHETIC" &&
            (item.authorityClass !== "DEMONSTRATION_ONLY" || !item.productionUseProhibited),
        )
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["widgets"],
          message: "Synthetic safety-review records must retain demonstration-only authority.",
        });
      }
    }
  });

export type SafetyReviewCategory = z.infer<typeof safetyReviewCategorySchema>;
export type SafetyReviewSignal = z.infer<typeof safetyReviewSignalSchema>;
export type SafetyReviewWidget = z.infer<typeof safetyReviewWidgetSchema>;
export type SafetyReviewResponse = z.infer<typeof safetyReviewResponseSchema>;
